/**
 * Old-school 16-bit pilot creator — mix gender, hair, clothes, dye.
 * Portraits live in public/assets/pilots/
 */

const BASE = (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL) || './';
const STORAGE_KEY = 'gs_pilot_look_v1';

function asset(path) {
    const b = BASE.endsWith('/') ? BASE : BASE + '/';
    const p = path.startsWith('/') ? path.slice(1) : path;
    return b + p;
}

export const PILOT_TYPES = [
    { id: 'man', label: 'Man' },
    { id: 'woman', label: 'Woman' },
];

export const PILOT_HAIR = [
    { id: 'buzz', label: 'Buzz', file: null }, // baked into suit/base
    { id: 'long', label: 'Long', file: 'hair_long' },
    { id: 'mohawk', label: 'Mohawk', file: 'hair_mohawk' },
    { id: 'spike', label: 'Spikes', file: 'hair_spike' },
    { id: 'tail', label: 'Ponytail', file: 'hair_tail' },
    { id: 'mullet', label: 'Mullet', file: 'hair_mullet', types: ['man'] },
    { id: 'bun', label: 'Bun', file: 'hair_bun', types: ['woman'] },
];

export const PILOT_SUITS = [
    { id: 'flight', label: 'Flight suit', file: 'base' },
    { id: 'leather', label: 'Leather', file: 'suit_leather' },
    { id: 'armor', label: 'Armor', file: 'suit_armor' },
    { id: 'captain', label: 'Captain', file: 'suit_captain' },
    { id: 'street', label: 'Street', file: 'suit_street' },
    { id: 'robe', label: 'Robes', file: 'suit_robe' },
];

export const PILOT_DYES = [
    { id: 'natural', label: 'Natural', tint: null },
    { id: 'blond', label: 'Blond', tint: '#e8c060' },
    { id: 'black', label: 'Black', tint: '#2a2430' },
    { id: 'crimson', label: 'Crimson', tint: '#cc2244' },
    { id: 'teal', label: 'Teal', tint: '#22c8c0' },
    { id: 'violet', label: 'Violet', tint: '#8844dd' },
    { id: 'white', label: 'White', tint: '#e8eef8' },
];

export const PILOT_TONES = [
    { id: 'fair', label: 'Fair', filter: 'contrast(1.02) saturate(0.92) brightness(1.08)' },
    { id: 'warm', label: 'Warm', filter: 'sepia(0.18) saturate(1.15) brightness(1.02)' },
    { id: 'tan', label: 'Tan', filter: 'sepia(0.28) saturate(1.2) brightness(0.92)' },
    { id: 'deep', label: 'Deep', filter: 'sepia(0.22) saturate(1.05) brightness(0.72) contrast(1.08)' },
];

export function defaultPilotLook() {
    return {
        name: 'Pilot',
        type: 'man',
        hair: 'buzz',
        suit: 'flight',
        dye: 'natural',
        tone: 'warm',
    };
}

export function loadPilotLook() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultPilotLook();
        return { ...defaultPilotLook(), ...JSON.parse(raw) };
    } catch {
        return defaultPilotLook();
    }
}

export function savePilotLook(look) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(look));
    } catch { /* quota */ }
    return look;
}

export function hairsForType(type) {
    return PILOT_HAIR.filter((h) => !h.types || h.types.includes(type));
}

export function randomPilotLook(prev = null) {
    const type = PILOT_TYPES[Math.floor(Math.random() * PILOT_TYPES.length)].id;
    const hairs = hairsForType(type);
    return {
        name: prev?.name || 'Pilot',
        type,
        hair: hairs[Math.floor(Math.random() * hairs.length)].id,
        suit: PILOT_SUITS[Math.floor(Math.random() * PILOT_SUITS.length)].id,
        dye: PILOT_DYES[Math.floor(Math.random() * PILOT_DYES.length)].id,
        tone: PILOT_TONES[Math.floor(Math.random() * PILOT_TONES.length)].id,
    };
}

function src(type, stem) {
    return asset(`assets/pilots/${type}_${stem}.png`);
}

export function suitSrc(look) {
    const suit = PILOT_SUITS.find((s) => s.id === look.suit) || PILOT_SUITS[0];
    return src(look.type, suit.file);
}

export function hairOverlaySrc(look) {
    const hair = PILOT_HAIR.find((h) => h.id === look.hair);
    if (!hair?.file) return null;
    return src(look.type, hair.file);
}

const images = {};
let loadPromise = null;

function loadImg(url) {
    return new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

export function loadPilotAssets() {
    if (loadPromise) return loadPromise;
    const urls = new Set();
    for (const t of PILOT_TYPES) {
        for (const s of PILOT_SUITS) urls.add(src(t.id, s.file));
        for (const h of hairsForType(t.id)) {
            if (h.file) urls.add(src(t.id, h.file));
        }
    }
    loadPromise = Promise.all([...urls].map((u) => loadImg(u).then((img) => { if (img) images[u] = img; })));
    return loadPromise;
}

export function getPilotImage(url) {
    return images[url] || null;
}

function tintHair(ctx, w, h, hex) {
    if (!hex) return;
    const c = hex.replace('#', '');
    if (c.length !== 6) return;
    const tr = parseInt(c.slice(0, 2), 16);
    const tg = parseInt(c.slice(2, 4), 16);
    const tb = parseInt(c.slice(4, 6), 16);
    const data = ctx.getImageData(0, 0, w, h);
    const p = data.data;
    for (let i = 0; i < p.length; i += 4) {
        const a = p[i + 3];
        if (a < 8) continue;
        const lum = (p[i] * 0.35 + p[i + 1] * 0.5 + p[i + 2] * 0.15) / 255;
        p[i] = Math.min(255, tr * lum * 1.15);
        p[i + 1] = Math.min(255, tg * lum * 1.15);
        p[i + 2] = Math.min(255, tb * lum * 1.15);
    }
    ctx.putImageData(data, 0, 0);
}

/**
 * Draw the composed pilot into a canvas (suit + optional hair overlay + dye).
 */
export function drawPilotPortrait(canvas, look, opts = {}) {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = false;

    const suitUrl = suitSrc(look);
    const suitImg = images[suitUrl];
    const hairUrl = hairOverlaySrc(look);
    const hairImg = hairUrl ? images[hairUrl] : null;
    const dye = PILOT_DYES.find((d) => d.id === look.dye);
    const tone = PILOT_TONES.find((t) => t.id === look.tone);

    ctx.save();
    if (tone?.filter) ctx.filter = tone.filter;

    if (suitImg) {
        ctx.drawImage(suitImg, 0, 0, w, h);
    } else {
        ctx.fillStyle = '#0a1428';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#7adfff';
        ctx.font = `${Math.round(h * 0.12)}px Orbitron, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('…', w / 2, h / 2);
    }

    ctx.filter = 'none';

    if (hairImg) {
        if (dye?.tint) {
            const off = document.createElement('canvas');
            off.width = w;
            off.height = h;
            const ox = off.getContext('2d');
            ox.imageSmoothingEnabled = false;
            ox.drawImage(hairImg, 0, 0, w, h);
            tintHair(ox, w, h, dye.tint);
            ctx.drawImage(off, 0, 0);
        } else {
            ctx.drawImage(hairImg, 0, 0, w, h);
        }
    }
    ctx.restore();

    if (opts.frame !== false) {
        ctx.strokeStyle = 'rgba(201,168,76,0.55)';
        ctx.lineWidth = Math.max(2, w * 0.02);
        ctx.strokeRect(1, 1, w - 2, h - 2);
    }
}

export function lookLabel(look) {
    const hair = PILOT_HAIR.find((h) => h.id === look.hair)?.label || look.hair;
    const suit = PILOT_SUITS.find((s) => s.id === look.suit)?.label || look.suit;
    const type = look.type === 'woman' ? 'Woman' : 'Man';
    return `${type} · ${hair} · ${suit}`;
}
